import React, { useState } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, TextField, Tooltip,
  Divider, InputAdornment, Stack, LinearProgress, List, ListItem,
  ListItemButton, ListItemText, Avatar, Badge,
} from '@mui/material';
import {
  PlayArrow as PlayIcon, SkipNext as StepIcon, Pause as PauseIcon,
  Stop as StopIcon, FiberManualRecord as RecordIcon,
  Refresh as RefreshIcon, Search as SearchIcon,
  CheckCircle as PassIcon, HourglassEmpty as RunningIcon,
  Circle as PendingIcon, Shield as ShieldIcon,
  CloudDone as CloudDoneIcon, Bolt as BoltIcon,
  Extension as ExtensionIcon, MoreVert as MoreIcon,
  Settings as SettingsIcon, Tune as TuneIcon,
} from '@mui/icons-material';
import { APP_LOGOS, CLIENT } from '../sharedMockData';

/**
 * Ext Admin — Sidebar Mockup (normal state)
 *
 * Staff / developer-facing extension surface. NOT what end-user inmates see
 * on facility Chromebooks — that runtime surface is in `ext-v5/`.
 * Width constrained to ~400px (Chrome side panel width).
 * Shows: runtime-fetched indicator, filter rules indicator, script picker,
 * step list, and transport bar. Useful for engineers debugging in real
 * Chrome tabs without needing to open DevTools alongside the dashboard.
 */

const EXTENSION_VERSION = '5.0.0';
const SIDEBAR_WIDTH = 400;
const PURPLE = '#7b1fa2';

const SCRIPTS = [
  { id: 1, name: 'Khan Login', app: 'khan', type: 'login', steps: 7, fresh: true },
  { id: 2, name: 'Khan Collect Progress', app: 'khan', type: 'progress', steps: 12, fresh: true },
  { id: 3, name: 'iCivics Account Creation', app: 'icivics', type: 'account-creation', steps: 14, fresh: true },
  { id: 4, name: 'iCivics Teacher Class Create', app: 'icivics', type: 'roster', steps: 9, fresh: true },
  { id: 5, name: 'ReadTheory Login', app: 'readtheory', type: 'login', steps: 5, fresh: false },
  { id: 6, name: 'LearnFree Login', app: 'learnfree', type: 'login', steps: 6, fresh: true },
  { id: 7, name: 'W3Schools Login', app: 'w3schools', type: 'login', steps: 4, fresh: true },
];

const KHAN_STEPS = [
  { num: 1, type: 'navigate', name: 'Navigate to Khan login', status: 'passed' },
  { num: 2, type: 'wait', name: 'Wait for identifier input', status: 'passed' },
  { num: 3, type: 'type', name: 'Enter username', status: 'running' },
  { num: 4, type: 'click', name: 'Click Continue', status: 'pending' },
  { num: 5, type: 'wait', name: 'Wait for password field', status: 'pending' },
  { num: 6, type: 'type', name: 'Enter password', status: 'pending' },
  { num: 7, type: 'assert', name: 'Verify login success', status: 'pending' },
];

const TYPE_COLORS = {
  navigate: { bg: '#e3f2fd', fg: '#1565c0' },
  click:    { bg: '#e3f2fd', fg: '#1565c0' },
  type:     { bg: '#e3f2fd', fg: '#1565c0' },
  wait:     { bg: '#fff3e0', fg: '#e65100' },
  assert:   { bg: '#e8f5e9', fg: '#2e7d32' },
  script:   { bg: '#f3e5f5', fg: '#6a1b9a' },
};

const STATUS_CONFIGS = {
  passed:  { icon: <PassIcon sx={{ fontSize: 16, color: '#4caf50' }} />, bg: '#f1f8e9' },
  running: { icon: <RunningIcon sx={{ fontSize: 16, color: '#ff9800' }} />, bg: '#fff8e1', border: '1px solid #ffc107' },
  pending: { icon: <PendingIcon sx={{ fontSize: 12, color: '#bdbdbd' }} />, bg: 'transparent' },
};

const ExtAdminSidebarMockup = () => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(1);
  const filteredScripts = SCRIPTS.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.app.toLowerCase().includes(search.toLowerCase()),
  );
  const selected = SCRIPTS.find(s => s.id === selectedId);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#eef1f5', p: 3, minHeight: 'calc(100vh - 180px)' }}>
      {/* Sidebar frame — 400px (Chrome side panel width) */}
      <Paper elevation={6} sx={{
        width: SIDEBAR_WIDTH, display: 'flex', flexDirection: 'column',
        bgcolor: '#fff', overflow: 'hidden', border: '1px solid #d5d9df',
        maxHeight: 'calc(100vh - 200px)',
      }}>
        {/* Toolbar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          bgcolor: PURPLE, color: '#fff',
        }}>
          <ExtensionIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: 0.2 }}>
            Automatrix
          </Typography>
          <Chip label={`v${EXTENSION_VERSION}`} size="small" sx={{
            height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.18)',
            color: '#fff', fontWeight: 600,
          }} />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Settings">
            <IconButton size="small" sx={{ color: '#fff' }}>
              <SettingsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="More">
            <IconButton size="small" sx={{ color: '#fff' }}>
              <MoreIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Runtime + filter indicators bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75,
          bgcolor: '#faf7fd', borderBottom: '1px solid #e8dff0',
        }}>
          <Tooltip title="Scripts fetched from /extension-scripts/export at runtime">
            <Chip
              icon={<CloudDoneIcon sx={{ fontSize: 14, color: '#4caf50 !important' }} />}
              label="Runtime-fetched · 2s ago"
              size="small"
              sx={{
                height: 22, fontSize: '0.68rem', bgcolor: '#e8f5e9',
                color: '#2e7d32', fontWeight: 600, borderRadius: 1,
                '& .MuiChip-icon': { ml: 0.5 },
              }}
            />
          </Tooltip>
          <Tooltip title="Refresh scripts now (re-fetch /extension/manifest)">
            <IconButton size="small" sx={{ p: 0.25, color: PURPLE }}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="DNR filter rules active (from /network-rule-sets/export/extension)">
            <Chip
              icon={<ShieldIcon sx={{ fontSize: 14, color: '#1976d2 !important' }} />}
              label="184 rules · 2s"
              size="small"
              sx={{
                height: 22, fontSize: '0.68rem', bgcolor: '#e3f2fd',
                color: '#0d47a1', fontWeight: 600, borderRadius: 1,
                '& .MuiChip-icon': { ml: 0.5 },
              }}
            />
          </Tooltip>
        </Box>

        {/* Script picker (searchable) */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #eceff2' }}>
          <TextField
            size="small" fullWidth placeholder="Search scripts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: '#90a4ae' }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.82rem', bgcolor: '#f7f9fb' },
            }}
          />
        </Box>

        {/* Script list (scrollable) */}
        <Box sx={{ maxHeight: 170, overflow: 'auto', borderBottom: '1px solid #eceff2' }}>
          <List dense disablePadding>
            {filteredScripts.map(s => {
              const isSel = s.id === selectedId;
              return (
                <ListItem key={s.id} disablePadding>
                  <ListItemButton
                    onClick={() => setSelectedId(s.id)}
                    selected={isSel}
                    sx={{
                      py: 0.5, px: 1.5,
                      borderLeft: isSel ? `3px solid ${PURPLE}` : '3px solid transparent',
                      '&.Mui-selected': { bgcolor: '#faf3fd' },
                    }}
                  >
                    <Avatar
                      src={APP_LOGOS[s.app]}
                      variant="rounded"
                      sx={{
                        width: 22, height: 22, mr: 1, fontSize: '0.65rem',
                        bgcolor: '#fff', border: isSel ? `1px solid ${PURPLE}` : '1px solid #e0e0e0',
                        fontWeight: 700,
                        '& img': { objectFit: 'contain', p: '1px' },
                      }}
                    >
                      {s.app.charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={isSel ? 700 : 500} sx={{ fontSize: '0.82rem' }}>
                          {s.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                          {s.app} · {s.steps} steps · {s.type}
                        </Typography>
                      }
                    />
                    {s.fresh && (
                      <Tooltip title="Fresh from server (< 60s)">
                        <BoltIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                      </Tooltip>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Selected script header */}
        <Box sx={{ px: 1.5, py: 1, bgcolor: '#f7f9fb', borderBottom: '1px solid #eceff2' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem', color: '#263238' }}>
              {selected.name}
            </Typography>
            <Chip label={selected.type} size="small" sx={{
              height: 18, fontSize: '0.62rem', bgcolor: '#ede7f6',
              color: '#5e35b1', fontWeight: 600,
            }} />
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
              {selected.steps} steps
            </Typography>
          </Stack>
        </Box>

        {/* Transport bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.75,
          borderBottom: '1px solid #eceff2', bgcolor: '#fff',
        }}>
          <Tooltip title="Play (run all)">
            <IconButton size="small" sx={{
              bgcolor: '#4caf50', color: '#fff', '&:hover': { bgcolor: '#388e3c' },
              width: 28, height: 28,
            }}>
              <PlayIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Step forward">
            <IconButton size="small" sx={{
              bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' },
              width: 28, height: 28,
            }}>
              <StepIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Pause">
            <IconButton size="small" sx={{ width: 28, height: 28 }}>
              <PauseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Stop">
            <IconButton size="small" sx={{ width: 28, height: 28 }}>
              <StopIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Record">
            <IconButton size="small" sx={{ color: '#f44336', width: 28, height: 28 }}>
              <RecordIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', mr: 0.5 }}>
            2/7
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(2 / 7) * 100}
            sx={{ width: 80, height: 4, borderRadius: 1 }}
          />
          <Tooltip title="Advanced (variables, conditions)">
            <IconButton size="small" sx={{ width: 28, height: 28 }}>
              <TuneIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Step list */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', px: 0.75, py: 0.75 }}>
          {KHAN_STEPS.map(step => {
            const sc = STATUS_CONFIGS[step.status];
            const tc = TYPE_COLORS[step.type] || TYPE_COLORS.click;
            return (
              <Box key={step.num} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.6,
                borderRadius: 1, mb: 0.4, bgcolor: sc.bg,
                border: sc.border || '1px solid transparent',
                cursor: 'pointer', '&:hover': { bgcolor: step.status === 'pending' ? '#f5f5f5' : sc.bg },
              }}>
                {sc.icon}
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#546e7a', minWidth: 14, fontSize: '0.7rem' }}>
                  {step.num}
                </Typography>
                <Chip label={step.type} size="small" sx={{
                  height: 16, fontSize: '0.58rem', bgcolor: tc.bg,
                  color: tc.fg, fontWeight: 700, textTransform: 'capitalize',
                }} />
                <Typography variant="body2" sx={{
                  flexGrow: 1, fontSize: '0.78rem',
                  fontWeight: step.status === 'running' ? 700 : 400,
                }}>
                  {step.name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Footer status strip */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
          borderTop: '1px solid #eceff2', bgcolor: '#f7f9fb',
        }}>
          <Badge color="success" variant="dot" sx={{ mr: 0.5 }}>
            <Box sx={{ width: 6, height: 6 }} />
          </Badge>
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#546e7a' }}>
            WS connected · cache hot
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#90a4ae', fontFamily: 'monospace' }}>
            org:{CLIENT.domain.split('.')[0]}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ExtAdminSidebarMockup;
